// @flow

import React from 'react';
import { Link } from '@reach/router';
import Table from 'antd/lib/table/Table';
import moment from 'moment';
import Header from '../../../components/layout/Header';
import { Section } from '../../../components/layout';
import Filter from './components/Filter';
import {
  getVehicleUtilization,
  exportVehicleUtilization
} from '../../../lib/api/vehicleUtilization';
import type {
  VehicleUtilizationFilter,
  VehicleFactUtilization
} from '../../../lib/types/vehicleUtilization';

type Props = {};

const initialParams: VehicleUtilizationFilter = {
  startDate: moment
    .utc()
    .startOf('month')
    .toISOString(),
  endDate: moment
    .utc()
    .endOf('month')
    .toISOString(),
  orgUnitIds: [],
  inventoryNumbers: []
};

const orderStatusDisplay = {
  created: 'Создана',
  approved: 'Назначена',
  cancelled: 'Отменена',
  approvingByMainEngineer: 'На подтверждении',
  approvedByMainEngineer: 'Подтверждено',
  approvedByContractVehicleOrder: 'Подтверждена заявкой на доп.найм',
  onReworkVehicle: 'На доработке',
  onReworkGPM: 'На доработке'
};

type State = {
  params: VehicleUtilizationFilter,
  data: Array<{
    key: string,
    orgUnitName: string,
    brandName: string,
    licensePlate: string,
    days: {
      [key: string]: ?{
        driver: string,
        status: string,
        orderId: ?number
      }
    }
  }>,
  days: Array<string>, // может содержать даты 'YYYY-MM-DD' или часы '0'..'23'
  loading: boolean,
  isHourMode: boolean // true – отображать часы, false – дни
};

export class VehicleUtilizationReport extends React.Component<Props, State> {
  state = {
    params: initialParams,
    data: [],
    days: [],
    loading: false,
    isHourMode: false
  };

  componentDidMount() {
    this.fetchData(this.state.params);
  }

  fetchData = async (params: VehicleUtilizationFilter) => {
    this.setState({ loading: true });
    try {
      const result: VehicleFactUtilization[] = await getVehicleUtilization(
        params
      );
      const { rows, days, isHourMode } = this.transformData(result, params);
      this.setState({ data: rows, days, loading: false, isHourMode });
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  transformData = (
    vehicles: VehicleFactUtilization[],
    params: VehicleUtilizationFilter
  ) => {
    const start = moment.utc(params.startDate);
    const end = moment.utc(params.endDate);

    // Определяем, выбран ли один день (без учёта времени)
    const isSingleDay = start.isSame(end, 'day');

    let days = [];
    let isHourMode = false;

    if (isSingleDay) {
      // Режим одного дня – 24 часа
      isHourMode = true;
      for (let h = 0; h < 24; h++) {
        days.push(String(h));
      }
    } else {
      // Обычный режим – дни от start до end
      let current = start.clone();
      while (current <= end) {
        days.push(current.format('YYYY-MM-DD'));
        current.add(1, 'day');
      }
    }

    const rows = vehicles.map((vehicle, idx) => {
      const daysMap = {};

      // Если режим часов – группируем по часам только для выбранного дня
      if (isHourMode) {
        const targetDate = start.clone().startOf('day');
        vehicle.dateInfos.forEach(info => {
          const dt = moment.utc(info.dateTime);
          // Проверяем, что дата совпадает с выбранным днём
          if (dt.isSame(targetDate, 'day')) {
            const hour = dt.hour();
            const key = String(hour);
            daysMap[key] = {
              driver: info.contractDriverFullName || '—',
              status:
                info.status != null
                  ? orderStatusDisplay[info.status] || `Неизв. (${info.status})`
                  : '—',
              orderId: info.orderId
            };
          }
        });
        // Заполняем null для отсутствующих часов
        for (let h = 0; h < 24; h++) {
          const key = String(h);
          if (!daysMap[key]) {
            daysMap[key] = null;
          }
        }
      } else {
        // Обычный режим – группировка по дням
        vehicle.dateInfos.forEach(info => {
          const dateKey = moment.utc(info.dateTime).format('YYYY-MM-DD');
          daysMap[dateKey] = {
            driver: info.contractDriverFullName || '—',
            status:
              info.status != null
                ? orderStatusDisplay[info.status] || `Неизв. (${info.status})`
                : '—',
            orderId: info.orderId
          };
        });
        // Заполняем null для отсутствующих дней
        days.forEach(d => {
          if (!daysMap[d]) {
            daysMap[d] = null;
          }
        });
      }

      return {
        key: `${idx}`,
        orgUnitName: vehicle.orgUnitName,
        brandName: vehicle.brandName,
        licensePlate: vehicle.licensePlate,
        days: daysMap
      };
    });

    return { rows, days, isHourMode };
  };

  handleChangeFilter = (key, value) => {
    this.setState(prevState => ({
      params: {
        ...prevState.params,
        [key]: value
      }
    }));
  };

  applyParams = async () => {
    await this.fetchData(this.state.params);
  };

  cleanParams = () => {
    this.setState({ params: initialParams }, () => {
      this.fetchData(initialParams);
    });
  };

  handleExport = async () => {
    try {
      await exportVehicleUtilization(this.state.params);
    } catch (error) {
      console.error('Ошибка экспорта', error);
    }
  };

  getColumns = () => {
    const { days, isHourMode } = this.state;
    const fixedColumns = [
      {
        title: 'Филиал',
        dataIndex: 'orgUnitName',
        fixed: 'left',
        width: 150
      },
      {
        title: 'Марка ТС',
        dataIndex: 'brandName',
        fixed: 'left',
        width: 120
      },
      {
        title: 'Гос. номер',
        dataIndex: 'licensePlate',
        fixed: 'left',
        width: 120
      }
    ];

    const dayColumns = days.map((key, index) => {
      let title;
      if (isHourMode) {
        // Режим часов – показываем время
        const hour = parseInt(key, 10);
        title = `${String(hour).padStart(2, '0')}:00`;
      } else {
        // Режим дней – показываем день и день недели
        const m = moment.utc(key);
        const dayNum = m.format('DD');
        const weekDay = m.format('dd');
        title = (
          <div style={{ textAlign: 'center' }}>
            <div>{dayNum}</div>
            <div style={{ fontSize: '0.7em', color: '#888' }}>{weekDay}</div>
          </div>
        );
      }

      return {
        title: title,
        key: key,
        dataIndex: key,
        onCell: () => ({ style: { minWidth: 90, padding: 0 } }),
        render: (text, record) => {
          const dayData = record.days[key];
          let bgColor = '#ffffff';
          let driverText = '—';

          if (dayData === null) {
            bgColor = '#d9d9d9';
          } else {
            driverText = dayData.driver;
            // Если заказ отсутствует (orderId == null), ячейка серая
            if (dayData.orderId == null) {
              bgColor = '#d9d9d9';
            } else {
              const status = dayData.status;
              if (status === 'Назначена') {
                bgColor = '#b7eb8f';
              } else if (status === 'Отменена') {
                bgColor = '#ffe58f';
              }
            }
          }

          const cellStyle = {
            backgroundColor: bgColor,
            padding: '6px 4px',
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: 1.4
          };

          if (dayData != null && dayData.orderId != null) {
            return (
              <Link
                to={`/orders/${dayData.orderId}/card`}
                style={{ ...cellStyle, textDecoration: 'none', color: 'inherit' }}
              >
                {driverText}
              </Link>
            );
          }

          return <div style={cellStyle}>{driverText}</div>;
        }
      };
    });

    return [...fixedColumns, ...dayColumns];
  };

  render() {
    const { params, data, loading } = this.state;
    const columns = this.getColumns();

    return (
      <>
        <Header left={<h1>Отчет о фактической занятости ТС</h1>} />
        <Section>
          <Filter
            params={params}
            onChangeFilter={this.handleChangeFilter}
            applyParams={this.applyParams}
            cleanParams={this.cleanParams}
            onExport={this.handleExport}
          />
          <Table
            loading={loading}
            pagination={{ pageSize: 50 }}
            dataSource={data}
            columns={columns}
            locale={{ emptyText: 'Нет данных для отображения' }}
            bordered
            scroll={{ x: 'max-content' }}  // ← Горизонтальная прокрутка
          />
        </Section>
      </>
    );
  }
}

export default VehicleUtilizationReport;